import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const navLinks = [
  { label: 'Product', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Docs', href: '/docs' },
  { label: 'Blog', href: '/blog' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { theme, cycle } = useTheme();

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Scroll to element with that ID for hash links; let React Router handle route links
  const handleClick = (e: React.MouseEvent, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 h-16 border-b border-border transition-all duration-300 bg-background/80 backdrop-blur-xl">
      <div className="px-6 lg:px-10 flex h-full items-center justify-between max-w-[1440px] mx-auto">
        {/* Left: Logo + byline */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-text-primary hover:opacity-80 transition-opacity shrink-0" onClick={() => setMobileOpen(false)}>
            <img src="/logo.png" alt="Koji" className="h-12 w-auto logo-animated" style={{ filter: 'drop-shadow(0 0 8px rgba(229,172,65,0.4))' }} />
          </Link>
          <span className="hidden sm:inline text-[11px] text-text-tertiary border-l border-border pl-4">
            Built by{' '}
            <a href="https://codedmotion.studio" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline font-medium">
              codedmotion.studio
            </a>
          </span>
        </div>

        {/* Center Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.href.startsWith('#') ? (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleClick(e, link.href)}
                className="text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* Right: Theme + Auth */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <button
            onClick={cycle}
            className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-secondary transition-colors"
            aria-label={`Theme: ${theme}`}
            title={`Theme: ${theme}. Click to cycle.`}
          >
            <ThemeIcon className="w-4 h-4" />
          </button>
          <Link to="/login" className="text-sm font-medium text-text-secondary hover:text-text-primary transition-colors" onClick={() => setMobileOpen(false)}>
            Sign In
          </Link>
          <Link to="/signup" className="text-sm font-semibold text-white px-5 py-2.5 rounded-lg bg-gradient-brand hover:shadow-cta hover:scale-[1.02] active:scale-[0.98] transition-all duration-200" onClick={() => setMobileOpen(false)}>
            Start Free Trial
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden p-2 text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-border shadow-xl animate-fade-in">
          <div className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.href.startsWith('#') ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleClick(e, link.href)}
                  className="text-base font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg px-3 py-2.5 transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg px-3 py-2.5 transition-colors"
                >
                  {link.label}
                </Link>
              )
            )}
            <hr className="border-border my-3" />
            <button
              onClick={cycle}
              className="flex items-center gap-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg px-3 py-2.5 transition-colors"
            >
              <ThemeIcon className="w-4 h-4" />
              Theme: {theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light'}
            </button>
            <Link to="/login" className="text-base font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg px-3 py-2.5 transition-colors" onClick={() => setMobileOpen(false)}>
              Sign In
            </Link>
            <Link to="/signup" className="text-center text-base font-semibold text-white px-5 py-2.5 rounded-lg bg-gradient-brand mt-2" onClick={() => setMobileOpen(false)}>
              Start Free Trial
            </Link>
            <p className="text-xs text-text-tertiary mt-3 text-center">
              Built by <a href="https://codedmotion.studio" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">codedmotion.studio</a>
            </p>
          </div>
        </div>
      )}
    </nav>
  );
}
